<template>
  <section v-if="template == null" :class="$style.empty">
    <p :class="$style.empty__text">閲覧するテンプレートを選択してください</p>
  </section>
  <section v-else :class="$style.template">
    <header :class="$style.header">
      <p :class="$style.label">{{ template.label }}</p>
      <MoreOptionsButton v-slot="{ closePanel }">
        <ul :class="$style.items">
          <li>
            <button
              type="button"
              name="edit"
              :class="$style.item"
              @click="handleEdit(closePanel)"
            >
              <IconPen />
              <p :class="$style.item__text">編集</p>
            </button>
          </li>
          <li>
            <button
              type="button"
              name="delete"
              @click="handleDelete(closePanel)"
              :class="[$style.item, $style['item--dustbox']]"
            >
              <IconDustBox />
              <p :class="$style.item__text">削除</p>
            </button>
          </li>
        </ul>
      </MoreOptionsButton>
    </header>
    <div :class="[$style.template__body, $style.body]">
      <p>{{ template.body }}</p>
      <ul :class="$style.selections">
        <li
          v-for="(selection, index) in template.selections"
          :key="index"
          :class="$style.selections__item"
        >
          <ChatMessageTemplateSelectionChip :selection="selection" />
        </li>
      </ul>
    </div>
    <footer
      v-if="!disableInsert"
      :class="[$style.template__footer, $style.footer]"
    >
      <FlatButton variant="primary" size="medium" @click="$emit('insert')">
        この内容で挿入
      </FlatButton>
    </footer>
  </section>
</template>

<script setup lang="ts">
import IconPen from '@/components/icons/IconPen.vue'
import IconDustBox from '@/components/icons/IconDustBox.vue'
import MoreOptionsButton from '@/components/molecules/MoreOptionsButton.vue'
import ChatMessageTemplateSelectionChip from './ChatMessageTemplateSelectionChip.vue'
import type { ChatMessageTemplate } from '@/types/app'
import type { ChatMessageTemplateModalMode } from './types'
import FlatButton from '@/components/atoms/FlatButton.vue'

defineProps<{
  template: Maybe<ChatMessageTemplate>
  disableInsert: boolean
}>()

const emit = defineEmits<{
  (event: 'insert'): void
  (event: 'changeMode', value: ChatMessageTemplateModalMode): void
}>()

const NOOP = () => {
  // do nothing
}

const handleEdit = (callback: () => void = NOOP) => {
  emit('changeMode', 'edit')
  callback()
}

const handleDelete = (callback: () => void = NOOP) => {
  emit('changeMode', 'delete')
  callback()
}
</script>

<style lang="scss" module>
.template {
  display: flex;
  flex-direction: column;

  &__body {
    overflow-y: scroll;
    flex: 1;
    padding: 16px 0;
    margin-top: 12px;
  }

  &__footer {
    border-top: 1px $color-border-low-emphasis solid;
    padding-top: 16px;
    display: flex;
    justify-content: flex-end;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  white-space: pre-wrap;
  word-break: break-all;
}

.label {
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
}

// 見えないしクリックできない状態にしたいが、３点リーダーボタンがないことが原因で
// テンプレタイトルの改行表示が若干変わるのを防ぐため、
// v-showではなくこのスタイルを当てる
.invisible {
  opacity: 0;
  pointer-events: none;
}

.body {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 14px;
  line-height: 24px;
}

.selections {
  margin-top: 16px;

  &__item {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px;
}

.item {
  display: flex;
  align-items: center;
  width: 50px;

  &--dustbox {
    color: $color-surface-accent-red;
  }

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}

.footer {
  &__button {
    &:nth-child(n + 2) {
      margin-left: 12px;
    }
  }
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;

  &__text {
    font-weight: bold;
  }
}
</style>
